import {HttpErrorResponse} from '@angular/common/http';
import {Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation} from '@angular/core';
import {FormArray, FormControl} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {FileUploadErrorMessage} from '@wba-ui-module/components/file-upload/file-upload-error-message';
import {MAX_CHAR_FOR_FILE_NAME} from '@wba/commons/vars/global.const';
import {HttpStatus} from '@wba/commons/vars/http-status.const';
import {Konvertierungsstatus} from '@wba/models/enums/Konvertierungsstatus.enum';
import {Level} from '@wba/models/enums/level.enum';
import {LogService} from '@wba/services/api/log.service';
import {NachweisService} from '@wba/services/api/nachweis/nachweis.service';
import {FileUtils} from '@wba/utils/file.utils';
import {firstValueFrom, interval, lastValueFrom, Observable, of, Subject, Subscription, timer} from 'rxjs';
import {catchError, concatMap, filter, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {FileUploadState} from '@wba-ui-module/components/file-upload/file-upload-state';
import {FileEntry} from '@wba-ui-module/components/file-upload/file-entry';
import {StateCounter} from '@wba-ui-module/components/file-upload/state-counter';
import {AnlageTyp} from '@wba/models/enums/anlage-typ.enum';
import {KontoauszuegeService} from '@wba/services/api/kontoauszuege/kontoauszuege.service';
import {KontoauszugDto} from '@wba/models/dto/konto-dto';
import {FileMetadata} from '@wba-ui-module/components/file-upload/file-metadata.model';

@Component({
  selector: 'geo-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class FileUploadComponent implements OnInit, OnDestroy {

  // make visible for component conditions
  protected readonly FileUploadState = FileUploadState;

  // timeout after 7min = 3sec * 140times
  private static readonly NUMBER_OF_RETRIES = 140;
  private static readonly TIME_IN_MS = 3000;
  private readonly unsubscribe$ = new Subject<void>();

  @ViewChild('dropzone') dropzoneElement: ElementRef<HTMLElement>;
  @ViewChild('input') inputElement: ElementRef<HTMLElement>;
  @ViewChild('list') listElement: ElementRef<HTMLElement>;
  @ViewChild('message') messageElement: ElementRef<HTMLElement>;

  @Input() id = '';
  @Input() parentId: string = null;
  @Input() kontext = '';
  @Input() parentForm = new FormArray<FormControl<FileEntry>>([]);

  @Input() warningAktivieren = false;
  @Input() dataAnalyticsName = '';
  @Input() label: string = 'Datei-Upload';
  @Input() screenReaderLabel: string = 'Dateien auswählen';
  @Input() hasError = false;

  protected dropable = false;

  private readonly sizeLimitInByte = 9 * 1024 * 1024;
  public errorMessage: string | null = null;
  public antragId = this.route.snapshot.queryParams.id;
  @Output() isLoadingEvent: EventEmitter<boolean> = new EventEmitter();
  @Output() loadingKontoauszuegeBeginEvent: EventEmitter<void> = new EventEmitter();
  @Output() loadingKontoauszuegeFinishedEvent: EventEmitter<void> = new EventEmitter();

  constructor(
    private readonly logService: LogService,
    private readonly nachweisService: NachweisService,
    private readonly kontoauszuegeService: KontoauszuegeService,
    private readonly route: ActivatedRoute,
    private readonly ngZone: NgZone
  ) {
  }

  async drop(event: Event) {
    this.handleDrag(event, false);
    await this.handleFileInput(event);
  }

  handleDrag(event: Event, dropable: boolean) {
    event.preventDefault();
    event.stopPropagation();
    this.dropable = dropable;
    return false;
  }

  updateLoadingStateMessage(counter: StateCounter) {
    if (counter.errors == 0) {
      this.screenReaderMessage = 'Alle Dateien erfolgreich hochgeladen.';
    } else {
      const plural = counter.errors > 1;
      this.screenReaderMessage = `
          ${counter.errors} Datei${plural ? 'en' : ''} wurde${plural ? 'n' : ''} nicht erfolgreich hochgeladen (${this.errorMessage || 'unbekannter Fehler'}).
          Bitte beachten Sie die Liste der hochgeladenen Dateien.`;
    }
  }

  set screenReaderMessage(text: string) {
    if (text && text !== this.messageElement.nativeElement.textContent) {
      this.messageElement.nativeElement.textContent = text;
    }
  }

  static changeFileExtensionToPDF(filename: string, maxLength?: number): string {
    const pos = filename.lastIndexOf('.');
    let filenameWithoutExtension = filename.substring(0, pos < 0 ? filename.length : pos);
    if (maxLength && filenameWithoutExtension.length > maxLength) {
      filenameWithoutExtension = filenameWithoutExtension.substring(0, maxLength);
    }
    return `${filenameWithoutExtension}.pdf`;
  }

  ngOnInit() {
    if (!this.kontext) {
      throw new Error('KONTEXT für Upload-Komponente nicht eingegeben');
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async download(item: FileEntry) {
    const file = await firstValueFrom(
      this.nachweisService.download(
        this.route.snapshot.queryParams.id,
        item.id,
        this.kontext
      ));

    const name = FileUploadComponent.changeFileExtensionToPDF(
      item.dateiname, MAX_CHAR_FOR_FILE_NAME);

    FileUtils.downloadFile(file, name);
  }

  async delete(file: any /*FormControl<FileEntry>*/, index: number) {
    this.parentForm.removeAt(index);
    const id = file.value.id;

    if (file.value.state == FileUploadState.SUCCESS) {
      await firstValueFrom(this.nachweisService.deleteFile(
        this.route.snapshot.queryParams.id, id, this.kontext).pipe(
        map(() => {
          if (this.kontext == AnlageTyp.KONTOAUSZUEGE) {
            firstValueFrom(this.kontoauszuegeService.deleteNachweisFromKontoDraft(this.parentId, id));
          }
        })
      ));
    }

    this.screenReaderMessage = `Datei ${file.value.dateiname} wurde erfolgreich gelöscht.`;
  }

  private validateAndUpload(file: File, counter: StateCounter): Promise<FileEntry> {
    const formData: FormData = new FormData();
    const metadata: FileMetadata = {
      originalFilename: file.name
    }

    formData.append('file', file, file.name);
    formData.append('metadata', new Blob([JSON.stringify(metadata)], {type: 'application/json'}));

    /* fileEntry ist Repräsentation der Datei in der Dateiliste, nicht das Formularelement */
    const fileEntry: FileEntry = {
      id: null,
      dateiname: file.name,
      state: FileUploadState.LOADING,
      validationMessage: null
    };

    // das folgende Push sorgt für die Ausführung des Observables, das von formValueChangedEvent in ngOnInit erzeugt wird
    this.parentForm.push(new FormControl<FileEntry>(fileEntry));
    const validationMessage = this.validateFileSize(file, this.sizeLimitInByte) ?? this.validateFileName(file);
    if (validationMessage == null) {
      this.isLoadingEvent.emit(true);
      return lastValueFrom(this.nachweisService
        .upload(formData, this.route.snapshot.queryParams.id, this.kontext, this.parentId)
        .pipe(
          switchMap((id: string) => {
            return this.waitTillConverting(id,
              (status: Konvertierungsstatus, timeout: boolean) => {
                this.handleStatus(id, status, timeout, counter, fileEntry);
                fileEntry.id = id;
                fileEntry.dateiname = FileUploadComponent.changeFileExtensionToPDF(file.name);
                this.parentForm.updateValueAndValidity();
              })
          }),
          map((_) => fileEntry),
          catchError((response: HttpErrorResponse) => {
            fileEntry.state = FileUploadState.ERROR;
            fileEntry.validationMessage = this.errorHandling(response);
            this.addMessageAndUpdatePage(fileEntry, counter);
            this.parentForm.updateValueAndValidity();
            this.validateLoadingState();
            throw new Error(fileEntry.validationMessage);
          }),
          tap(() => this.validateLoadingState())
        ));
    } else {
      fileEntry.state = FileUploadState.ERROR;
      fileEntry.validationMessage = this.validateFileSize(file, this.sizeLimitInByte);
      this.addMessageAndUpdatePage(fileEntry, counter);
      return Promise.reject(new Error(fileEntry.validationMessage));
    }
  }

  private validateLoadingState() {
    const fileLoading = this.parentForm.controls.find(fileEntry => {
      return fileEntry.value.state == FileUploadState.LOADING
    })
    if (fileLoading == undefined) {
      this.isLoadingEvent.emit(false);
    }
  }

  async handleFileInput(event: any) {
    const fileList: FileList = event.target.files || event.dataTransfer.files;
    const counter = new StateCounter();

    let promises: Promise<FileEntry>[];
    if (this.kontext == AnlageTyp.KONTOAUSZUEGE) {
      promises = this.handleFileInputForKontoauszuege(fileList, counter);
      await Promise.all(promises);
    } else {
      promises = [];
      for (let file of Array.from(fileList)) {
        promises.push(this.validateAndUpload(file, counter));
      }
    }
    await Promise.all(promises);

    this.ngZone.run(() => this.updateLoadingStateMessage(counter));
    // Ruft @handleFileInput auf
    event.target.value = '';
  }

  private handleFileInputForKontoauszuege(fileList: FileList, counter: StateCounter): Promise<FileEntry>[] {
    let fileEntryPromises: Promise<FileEntry>[] = [];

    // Bei Chrome und Edge gibt es in Problem, dass die fileList leer ist nach dem Promise. In Firefox klappt es.
    // Mit der copy bleibt die list überall erhalten
    const fileListConst = Array.from(fileList);
    lastValueFrom(this.kontoauszuegeService.getKontoDraft(this.parentId))
      .then(konto => {
        this.loadingKontoauszuegeBeginEvent.emit();
        for (let file of Array.from(fileListConst)) {
          let fileEntryPromise = this.validateAndUpload(file, counter);
          fileEntryPromises.push(fileEntryPromise);
          fileEntryPromise.then(fileEntry => {
            konto.nachweise.push({id: fileEntry.id, dateiname: fileEntry.dateiname} as KontoauszugDto);
          }).catch((error) => console.log("Fehler bei Upload eines Nachweises:", error.message ?? error));
        }

        // Periodischer Aufruf noetig, weil ein Update des kontoDrafts nach jedem abgeschlossenem Upload zu
        // Race-Conditions fuehrt. Dies hat zur Folge, dass an der nachweise-Liste des kontoDrafts einige Referenzen auf
        // Nachweise fehlen.
        let subscriptions: Subscription = new Subscription();
        let stopPeriodicalUpdate$ = of(0);
        Promise.any(fileEntryPromises).then(() => {
          const subscription = interval(1000).pipe(
            takeUntil(stopPeriodicalUpdate$)
          ).subscribe(() => {
            lastValueFrom(this.kontoauszuegeService.updateKontoDraft(konto))
              .catch((error) => console.log("Fehler bei kontoDraft-Update:", error.message ?? error))
          });
          subscriptions.add(subscription);
        }).catch((error) => console.log("Alle Nachweis-Uploads fehlerhaft:", error.message ?? error));

        // allSetteled() statt all(), weil das kontoDraft Update auch ausgefuehrt werden muss, wenn fehlerhafte Uploads dabei waren
        Promise.allSettled(fileEntryPromises)
          .then(() => {
            stopPeriodicalUpdate$.subscribe();
            lastValueFrom(this.kontoauszuegeService.updateKontoDraft(konto))
              .catch((error) => console.log("Fehler bei kontoDraft-Update:", error.message ?? error))
          }).catch((error) => console.log("Fehler bei Verarbeitung der Nachweis-Uploads:", error.message ?? error))
          .finally(() => {
              subscriptions.unsubscribe();
              this.loadingKontoauszuegeFinishedEvent.emit();
            }
          );

      }).catch((error) => console.log("Fehler bei kontoDraft-Abruf:", error.message ?? error));
    return fileEntryPromises;
  }

  private validateFileName(file: File, nameLengthLimitInByte = 255): string {

    const nameByteLength = new TextEncoder().encode(file.name).byteLength
    if (nameByteLength > nameLengthLimitInByte) {
      return FileUploadErrorMessage.DATEI_NAME_ZU_LANG;
    }
  }

  private validateFileSize(file: File, sizeLimitInByte = 9 * 1024 * 1024): string {

    if (file.size === 0) {
      return FileUploadErrorMessage.DATEI_LEER;
    }
    if (file.size > sizeLimitInByte) {
      return FileUploadErrorMessage.ZU_GROSS;
    }
    return null;
  }

  private handleStatus(value: string, status: Konvertierungsstatus, timeout: boolean, counter: StateCounter, fileEntry: FileEntry) {
    if (status === Konvertierungsstatus.ERROR || timeout) {
      fileEntry.state = FileUploadState.ERROR;
      fileEntry.validationMessage = 'Konvertierung fehlgeschlagen';
      try {
        const nachweisRequest = this.logService.nachweisRequests.get(value);
        this.logService.nachweisRequests.set(value, {
          ...nachweisRequest,
          milliSecondsBtwnRequestAndErrorResponse: Date.now() - nachweisRequest.startRequestTime.getTime()
        });

        this.logService.log({
          level: Level.ERROR,
          message: JSON.stringify({
            ...this.logService.nachweisRequests.get(value),
            konvertierungsstatus: status,
            isTimeout: timeout
          })
        });
      } catch (e) {
      } finally {
        this.appendErrorMessage(FileUploadErrorMessage.TECHNISCHER_FEHLER, counter);
      }
    } else {
      fileEntry.state = FileUploadState.SUCCESS;
      counter.success();
    }
  }

  private waitTillConverting(nachweisId: string, callbackFn: Function): Observable<any> {
    let i = 0;
    return timer(0, FileUploadComponent.TIME_IN_MS)
      .pipe(concatMap(() => this.nachweisService.convertStatus(this.route.snapshot.queryParams.id, nachweisId, this.kontext)))
      .pipe(tap(_ => i++))
      .pipe(filter(backendData => (backendData.status !== Konvertierungsstatus.IN_PROGRESS) || i >= FileUploadComponent.NUMBER_OF_RETRIES))
      .pipe(take(1))
      .pipe(tap(({status}) => callbackFn(status, i === FileUploadComponent.NUMBER_OF_RETRIES)));
  }

  private errorHandling(response: HttpErrorResponse): string | null {
    const url = new URL(response.url);
    const path = url.pathname;

    if (path.endsWith('/nachweis') || path.endsWith('/nachweis/konvertierungsstatus')) {
      const startRequestTime = url.searchParams.get('startRequestTime');
      const key: string = new Date(startRequestTime).getTime().toString();

      try {
        const nachweisRequest = this.logService.nachweisRequests.get(key);
        this.logService.nachweisRequests.set(key, {
          ...nachweisRequest,
          milliSecondsBtwnRequestAndErrorResponse: Date.now() - nachweisRequest.startRequestTime.getTime()
        });
        this.logService.log({
          level: Level.ERROR,
          message: JSON.stringify(this.logService.nachweisRequests.get(key))
        });
      } catch (e) {
        return 'Es ist ein technischer Fehler aufgetreten.';
      }
    }

    if (response.status === HttpStatus.BAD_REQUEST) {
      const error = response.error["0"] ?? response.error.error[0];
      return error?.nachricht ?? FileUploadErrorMessage.PASSWORT_GESCHUETZT;
    }

    const errorCode = response.headers.get('ErrorCode');
    if (!this.checkOtherRequestErrors(errorCode)) {
      return this.foundErrorServiceValidation(response);
    }
    return null;
  }

  private checkOtherRequestErrors(errorCode: string): string {
    if (!errorCode) {
      return null;
    }
    if (errorCode === '1048') {
      return FileUploadErrorMessage.ZU_GROSS;
    } else if (errorCode === '1070') {
      return FileUploadErrorMessage.PASSWORT_GESCHUETZT;
    }
    return FileUploadErrorMessage.TECHNISCHER_FEHLER;
  }

  private foundErrorServiceValidation(response: HttpErrorResponse): string {
    if (response.status === HttpStatus.NOT_FOUND) {
      if ((response.error as Object).hasOwnProperty('listeProbleme')) {
        return FileUploadErrorMessage.FALSCHES_FORMAT;
      }
    } else {
      return FileUploadErrorMessage.PASSWORT_GESCHUETZT;
    }
  }

  private appendErrorMessage(message: string, counter: StateCounter): void {
    counter.error();
    if (this.errorMessage?.trim().length > 0) {
      this.errorMessage += '<br>, ' + message;
    } else {
      this.errorMessage = message;
    }
  }

  private addMessageAndUpdatePage(fileEntry: FileEntry, counter: StateCounter) {
    this.appendErrorMessage(fileEntry.validationMessage, counter);
    this.ngZone.run(() => this.updateLoadingStateMessage(counter));
  }

  public isSuccessfulUpload(file: FormControl): boolean {
    return file.value.state == FileUploadState.SUCCESS || file.value.state == null;
  }
}
